import {useAlert, useAuth} from "../../context";
import React, {useState} from "react";
import {Button, Col, Form, InputGroup, Row, Modal, Stack} from "react-bootstrap";
import {ResponsiveContent} from "../ResponsiveNavTabs";
import {regex} from "../../utils2";
import {ColumnizeText, PageTitle, SingleText} from "../_components";
import {Formik, Form as FormikForm, Field} from "formik";
import {ButtonDelete} from "../_components";
import * as yup from "yup";
import {useBrowserTitle} from "../../hooks/useBrowserTitle";


export function Profile() {
    useBrowserTitle("Profile")
    return <ResponsiveContent body={<ProfileForm/>}/>
}

function ProfileForm() {

    const {success, warn, error} = useAlert();
    const {user, updateUserSelf, deleteUserSelf, logout} = useAuth();

    const [show, setShow] = useState(false);
    const [editEmail, setEditEmail] = useState(true);
    const [editPassword, setEditPassword] = useState(true)

    const validationSchema = yup.object().shape({
        username: yup.string()
            .required("Email is mandatory")
            .email("Email not valid"),
        password: yup.string()
            //.test("required", "A valid password is required", value => !(editPassword && (value === "" || value === undefined)))
            //.min(8, 'password must contain 8 or more characters with at least one of each: uppercase, lowercase, number and special')
            .matches(regex.password_pattern, "Password must be 8+ characters, with a number, uppercase, lowercase, and a special character"),
        first_name: yup.string()
            .required("First name is missing"),
        last_name: yup.string()
            .required("Last name is missing"),
        // location: yup.string(),
    })

    function handleOnSubmit(values, actions) {
        updateUserSelf(values.first_name, values.last_name, values.location, values.username, values.password)
            .then(() => {
                success("Profile updated correctly!")
                if (values.username !== user.username) {
                    warn("Verification email sent.")
                    logout()
                } else if (values.password !== "") {
                    logout()
                }
            })
            .catch(err => {
                if (err.message.includes("already registered")) {
                    warn(<p>It seems <i>{values.username}</i> is already picked.<br/>Use a different email address.</p>);
                } else {
                    error(<p>Server message: <i>{err.message}</i></p>, "Server error");
                }
            })
            .finally(() => actions.setSubmitting(false))
    }

    function handleDeleteAccount() {
        deleteUserSelf().then(resp => {
            if (resp.success) {
                success("Your account has been permanently deleted from our servers.", "Account Removed Successfully");
                logout()
            }
        })
    }

    return (
        <>
            <PageTitle title="Profile">Review or update your details.</PageTitle>

            <Formik validationSchema={validationSchema} onSubmit={handleOnSubmit} initialValues={user} enableReinitialize={true}>
                {({isSubmitting, resetForm}) => (
                    <FormikForm>

                        <ColumnizeText>
                            <SingleText name="first_name" label="Firstname" maxLength="64"/>
                            <SingleText name="last_name" label="Lastname" maxLength="64"/>
                        </ColumnizeText>

                        <SingleText name="location" label="Description" type="textarea" placeholder="Radio amateur"/>

                        <Row>
                            <Col md={6}>
                                <Field name="username">
                                    {({field, meta}) => <Form.Group controlId="usernameId" className="mb-2">
                                        <Form.Label column="">Email</Form.Label>
                                        <InputGroup hasValidation>
                                            <ConfirmationButton onConfirmation={setEditEmail} disabled={!editEmail}>By clicking <i>Yes</i> you can edit
                                                email. <br/><br/> After clicking <i>Update Account</i> you will be logged out and a new validation email will be
                                                sent.</ConfirmationButton>
                                            <Form.Control {...field} type="email" isInvalid={meta.touched && !!meta.error} placeholder="email@example.com"
                                                          autoComplete="off"
                                                          maxLength="128" disabled={editEmail}/>
                                            <Form.Control.Feedback type="invalid">{meta.error}</Form.Control.Feedback>
                                        </InputGroup>
                                    </Form.Group>}
                                </Field>
                            </Col>

                            <Col>
                                <Field name="password">
                                    {({field, meta}) => <Form.Group controlId="passwordId" className="mb-2">
                                        <Form.Label column="">Password</Form.Label>
                                        <InputGroup hasValidation>
                                            <ConfirmationButton onConfirmation={setEditPassword} disabled={!editPassword}>By clicking <i>Yes</i> you can edit
                                                password. <br/><br/> After clicking <i>Update Account</i> you will be logged out.</ConfirmationButton>
                                            <Form.Control {...field} type={show ? "text" : "password"} isInvalid={meta.touched && !!meta.error}
                                                          autoComplete="new-password"
                                                          maxLength="128" minLength="8" disabled={editPassword}/>
                                            <Button variant={show ? "secondary" : "outline-secondary"} id="showId" checked={show} disabled={editPassword}
                                                    onClick={() => setShow(show => !show)}>
                                                {show ?
                                                    <>
                                                        <span className="d-none d-sm-block">Hide</span>
                                                        <span className="bi-eye-slash d-block d-sm-none"/>
                                                    </> :
                                                    <>
                                                        <span className="d-none d-sm-block">Show</span>
                                                        <span className="bi-eye d-block d-sm-none"/>
                                                    </>
                                                }
                                            </Button>
                                            <Form.Control.Feedback type="invalid">{meta.error}</Form.Control.Feedback>
                                        </InputGroup>
                                    </Form.Group>}
                                </Field>
                            </Col>
                        </Row>

                        <Stack direction="horizontal" gap="2" className="py-3 justify-content-end">
                            <Button type="reset" variant="secondary" onClick={resetForm}>Reset</Button>
                            <ButtonDelete variant="danger" onDelete={handleDeleteAccount} value={user.username}>Delete</ButtonDelete>
                            <Button type="submit" variant="success" disabled={isSubmitting}>Update Account</Button>
                        </Stack>

                    </FormikForm>
                )}
            </Formik>
        </>
    )
}


function ConfirmationButton({onConfirmation, disabled, children}) {

    const [show, setShow] = useState(false)

    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    function handleEdit() {
        onConfirmation(false)
        setShow(false)
    }

    return <>
        <Button variant="primary" onClick={handleShow} disabled={disabled}>
            <span className="d-none d-sm-block">Edit</span>
            <span className="bi-pencil d-block d-sm-none"/>
        </Button>

        <Modal show={show} onHide={handleClose} backdrop="static" keyboard={true}>
            <Modal.Header closeButton>
                <Modal.Title>Are you sure?</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {children}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>No</Button>
                <Button variant="primary" onClick={handleEdit}>Yes</Button>
            </Modal.Footer>
        </Modal>
    </>
}
